<template>
    <div class="position-relative">
        <div @dragover.prevent.stop="dragover"
             @dragleave.prevent.stop="dragleave"
             @drop.prevent.stop="drop"
             :class="{ dragging: isDragover, 'app-upload-area-box': showContainerBox }"
             class="app-upload-area">
            <input @change="onSelectFiles"
                   type="file"
                   name="file"
                   id="file"
                   ref="file"
                   accept="application/pdf, text/csv, image/*"
                   multiple
                   class="d-none">

            <slot name="upload-area" :browse="toggleFileBrowser"/>

        </div>

        <slot></slot>

        <slot name="upload-info" :files="files" :filesUploadInfo="filesUploadInfo" :clear="onClearUploadInfo"/>
    </div>
</template>

<script>
import axios from 'axios'
import sha1 from 'sha1'
import {
    DOC_UPLOAD_STATUS_ACCEPTED,
    DOC_UPLOAD_STATUS_DUPLICATE,
    DOC_UPLOAD_STATUS_ERROR,
    DOC_UPLOAD_STATUS_SUCCESS,
    DOC_UPLOAD_STATUS_UPLOADING
} from './documentUploadStatuses'
import {getRandomId} from '@/utils'
import {AppDocSystemTypes, getDocSystemTypeDefaultModel} from '@/types/document/types'
import {showInfoMessage} from '@/services/snackbar'

export default {
    name: 'DocUploadContainer',
    props: {
        docTypeKey: {
            type: String,
            default: AppDocSystemTypes.PURCHASING
        },
        docStatusKey: {
            type: String,
            required: false,
            default: ''
        },
        docSubmoduleKey: {
            type: String,
            required: false,
            default: ''
        },
        docUuid: { // NOTE: if specified => upload like attachments to specified document
            type: String,
            required: false,
            default: ''
        },
        locationUuid: {
            type: String,
            required: false,
            default: ''
        },
        subsidiaryUuid: {
            type: String,
            required: false,
            default: ''
        },
        showContainerBox: {
            type: Boolean,
            default: true
        },
        clearSuccessfullyUploaded: {
            type: Boolean,
            default: false
        },
        onSuccessUpload: {
            type: Function
        }
    },
    data() {
        return {
            files: {},
            filesUploadInfo: {},
            isDragover: false,
            batchSize: 5
        }
    },
    computed: {
        hasNotUploadedFiles() {
            return Object.values(this.filesUploadInfo).some(info => [DOC_UPLOAD_STATUS_ACCEPTED, DOC_UPLOAD_STATUS_UPLOADING].includes(info.status))
        }
    },
    methods: {
        toggleFileBrowser() {
            this.$refs.file.click()
        },

        dragover() {
            this.isDragover = true
        },

        dragleave() {
            this.isDragover = false
        },

        withNotUploadedFilesCheck(fun) {
            if (this.hasNotUploadedFiles) {
                showInfoMessage('Please wait for the queue.')
                return false
            } else {
                if (typeof fun === 'function') {
                    fun()
                }
            }
        },

        drop(e) {
            this.isDragover = false
            this.withNotUploadedFilesCheck(() => {
                for (const file of e.dataTransfer.files) {
                    this.addFileToUpload(file)
                }
                this.uploadFiles()
            })
        },

        onSelectFiles(e) {
            this.withNotUploadedFilesCheck(() => {
                for (const file of e.target.files) {
                    this.addFileToUpload(file)
                }
                this.uploadFiles()
            })
        },

        onClearUploadInfo() {
            this.withNotUploadedFilesCheck(() => {
                this.filesUploadInfo = {}
                this.files = {}
            })
        },

        addFileToUpload(file) {
            const fileId = getRandomId('file')
            this.$set(this.files, fileId, file)
            this.$set(this.filesUploadInfo, fileId, {
                status: DOC_UPLOAD_STATUS_ACCEPTED,
                percentage: 0,
                // NOTE: (!) save to upload with correct options if route was changed during the uploading (user navigates between document type sections case)
                docTypeKey: this.docTypeKey,
                docStatusKey: this.docStatusKey,
                docSubmoduleKey: this.docSubmoduleKey,
                locationUuid: this.locationUuid,
                subsidiaryUuid: this.subsidiaryUuid
            })
        },

        async uploadFiles() {
            const fileIdGroups = _.chunk(Object.keys(this.files), this.batchSize)
            for (const fileIds of fileIdGroups) {
                await this.uploadFilesBatch(fileIds)
            }
        },

        uploadFilesBatch(fileIds) {
            const promises = []
            for (const fileId of fileIds) {
                if (this.docUuid) {
                    promises.push(this.uploadAttachment(fileId))
                } else {
                    promises.push(this.uploadDoc(fileId))
                }
            }
            return Promise.all(promises)
        },

        async uploadDoc(fileId) {
            const file = this.files[fileId]
            if (!file) {
                return
            }
            const info = this.filesUploadInfo[fileId]
            if (info.status !== DOC_UPLOAD_STATUS_ACCEPTED) { // prevent uploading already processed files (user can add more files to upload in a few steps)
                return
            }

            const formData = new FormData()
            const section = getDocSystemTypeDefaultModel(info.docTypeKey)
            formData.append('invoice', file)
            formData.append('type', section.key)
            formData.append('section', section.key)
            if (info.docStatusKey) {
                formData.append('status', info.docStatusKey)
            }
            if (info.docSubmoduleKey) {
                formData.append('submodule', info.docSubmoduleKey)
            }
            if (info.locationUuid) {
                formData.append('location', info.locationUuid)
            }
            if (info.subsidiaryUuid) {
                formData.append('subsidiary', info.subsidiaryUuid)
            }
            this.$set(info, 'status', DOC_UPLOAD_STATUS_UPLOADING)
            return axios.post('documents/upload', formData, {
                onUploadProgress: e => {
                    this.$set(info, 'percentage', Math.round((e.loaded * 100) / e.total))
                }
            })
                .then(res => {
                    this.$set(info, 'status', DOC_UPLOAD_STATUS_SUCCESS)
                    const uuid = _.get(res, 'data.data.invoice.uuid')
                    if (uuid && typeof this.onSuccessUpload === 'function') { // used in related doc uploads
                        return this.onSuccessUpload({uuid, section: info.docTypeKey})
                    }
                })
                .then(() => {
                    if (this.clearSuccessfullyUploaded) {
                        setTimeout(() => {
                            this.$delete(this.files, fileId)
                            this.$delete(this.filesUploadInfo, fileId)
                        }, 2000)
                    }
                    this.$emit('uploaded')
                })
                .catch(e => {
                    const statusCode = _.get(e, 'response.status')
                    if (statusCode === 429) {
                        this.$set(info, 'status', DOC_UPLOAD_STATUS_DUPLICATE)
                        this.$set(info, 'document', _.get(e, 'response.data.document'))
                    } else if (statusCode === 422) {
                        this.$set(info, 'status', DOC_UPLOAD_STATUS_ERROR)
                        this.$set(info, 'details', _.get(e, 'response.data.message'))
                    } else {
                        this.$set(info, 'status', DOC_UPLOAD_STATUS_ERROR)
                        this.$raven.captureException(e)
                    }
                })
        },

        async uploadAttachment(fileId) {
            const file = this.files[fileId]
            if (!file) {
                return
            }
            const info = this.filesUploadInfo[fileId]
            if (info.status !== DOC_UPLOAD_STATUS_ACCEPTED) { // prevent uploading already processed files (user can add more files to upload in a few steps)
                return
            }

            return new Promise(resolve => {
                const reader = new FileReader()
                reader.readAsBinaryString(file)
                reader.onerror = () => {
                    this.$set(info, 'status', DOC_UPLOAD_STATUS_ERROR)
                    resolve()
                }
                reader.onload = async e => {
                    const content = e.target.result
                    this.$set(info, 'status', DOC_UPLOAD_STATUS_UPLOADING)
                    // eslint-disable-next-line camelcase
                    const content_type = file.type || 'image/jpeg'

                    try {
                        const sign = await axios.post('uploads/sign', {content_type})
                        const instance = await axios.create({baseURL: sign.data.url})
                        instance.defaults.headers.common = sign.data.headers
                        await instance.put(sign.data.url, file, {
                            onUploadProgress: e => {
                                this.$set(info, 'percentage', Math.round((e.loaded * 100) / e.total))
                            }
                        })
                        const section = getDocSystemTypeDefaultModel(info.docTypeKey)
                        let data = {
                            file: {
                                key: sign.data.key,
                                uuid: sign.data.uuid,
                                bucket: sign.data.bucket,
                                name: file.name,
                                mime: content_type,
                                hash: sha1(content)
                            },
                            object: {
                                uuid: this.docUuid
                            }
                        }
                        const result = await axios.post('uploads/attachment', data)
                        if (result && result.data && result.data.uuid && typeof this.onSuccessUpload === 'function') { // used in related doc uploads
                            try {
                                await this.onSuccessUpload({uuid: result.data.uuid, section: info.docTypeKey})
                            } catch (e) {
                                this.$raven.captureException(e)
                            }
                        }
                        this.$set(info, 'status', DOC_UPLOAD_STATUS_SUCCESS)
                        if (this.clearSuccessfullyUploaded) {
                            setTimeout(() => {
                                this.$delete(this.files, fileId)
                                this.$delete(this.filesUploadInfo, fileId)
                            }, 2000)
                        }
                        this.$emit('uploaded')
                    } catch (e) {
                        if (e.response && e.response.status === 429) {
                            this.$set(info, 'status', DOC_UPLOAD_STATUS_DUPLICATE)
                        } else {
                            this.$set(info, 'status', DOC_UPLOAD_STATUS_ERROR)
                        }
                    } finally {
                        resolve()
                    }
                }
            })
        }
    }
}
</script>
